// components/features/sms/SmsRow.tsx

import { useState } from "react";
import TableRow from "../../ui/TableRow";
import { Send, CheckCircle, XCircle, Clock, RefreshCw, Eye, Copy, ChevronDown, ChevronUp } from "lucide-react";
import { alertSuccess } from "../../../helpers/alertError";
import { Sms } from "../../../types/features/Sms";

interface SmsRowProps {
  sms: Sms;
  onAction: (sms: Sms) => void;
  onResend?: (smsId: string, recipientId: string) => Promise<void>;
  onSelect?: (sms: Sms, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

export default function SmsRow({
  sms,
  onAction,
  onResend,
  onSelect,
  isSelected = false,
  selectable = false,
}: SmsRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [resending, setResending] = useState<string | null>(null);

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-700",
    sent: "bg-blue-100 text-blue-700",
    delivered: "bg-green-100 text-green-700",
    failed: "bg-red-100 text-red-700",
  };

  const statusIcons = {
    pending: <Clock size={14} className="text-yellow-500" />,
    sent: <Send size={14} className="text-blue-500" />,
    delivered: <CheckCircle size={14} className="text-green-500" />,
    failed: <XCircle size={14} className="text-red-500" />,
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      pending: "En attente",
      sent: "Envoyé",
      delivered: "Délivré",
      failed: "Échoué",
    };
    return labels[status as keyof typeof labels] || status;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-medium ${statusColors[status as keyof typeof statusColors]}`}
      >
        {statusIcons[status as keyof typeof statusIcons]}
        {getStatusLabel(status)}
      </span>
    );
  };

  const totalRecipients = sms.recipients?.length || 0;
  const deliveredCount = sms.recipients?.filter((r) => r.status === "delivered").length || 0;
  const failedCount = sms.recipients?.filter((r) => r.status === "failed").length || 0;
  const pendingCount = sms.recipients?.filter((r) => r.status === "pending").length || 0;
  const sentCount = sms.recipients?.filter((r) => r.status === "sent").length || 0;

  const handleResend = async (recipientId: string) => {
    if (!onResend) return;
    if (!confirm("Renvoyer ce SMS ?")) return;
    setResending(recipientId);
    try {
      await onResend(sms.id, recipientId);
      alertSuccess("SMS renvoyé avec succès");
    } catch (error) {
      console.error("Erreur lors du renvoi", error);
    } finally {
      setResending(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alertSuccess("Copié !");
  };

  return (
    <TableRow
      item={sms}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      {/* Message */}
      <td className="py-3 px-4">
        <div>
          <span className="font-medium text-foreground block truncate max-w-xs">
            {sms.message.length > 60 ? sms.message.substring(0, 60) + "..." : sms.message}
          </span>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
            <span className="text-xs text-muted-foreground">{totalRecipients} destinataire(s)</span>
            {sms.source_type && (
              <>
                <span>•</span>
                <span className="capitalize text-xs">{sms.source_type}</span>
              </>
            )}
            <span>•</span>
            <span className="text-xs">{formatDate(sms.created_at)}</span>
          </div>
        </div>
      </td>

      {/* Statuts résumés */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-2 flex-wrap">
          {deliveredCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle size={12} />
              {deliveredCount}
            </span>
          )}
          {sentCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-blue-600">
              <Send size={12} />
              {sentCount}
            </span>
          )}
          {pendingCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-yellow-600">
              <Clock size={12} />
              {pendingCount}
            </span>
          )}
          {failedCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-red-600">
              <XCircle size={12} />
              {failedCount}
            </span>
          )}
          {totalRecipients === 0 && <span className="text-xs text-gray-400">Aucun destinataire</span>}
        </div>
      </td>

      {/* Actions */}
      <td className="py-3 px-4 text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => onAction(sms)}
            className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
            title="Voir les détails"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            title={expanded ? "Réduire" : "Développer"}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </td>

      {/* Ligne développée - Destinataires */}
      {expanded && (
        <tr>
          <td colSpan={3} className="py-3 px-4 bg-gray-50">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Destinataires</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {sms.recipients?.map((recipient) => (
                  <div
                    key={recipient.id}
                    className="flex items-center justify-between bg-white p-2 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      {getStatusBadge(recipient.status)}
                      <span className="text-sm font-mono text-gray-700">{recipient.to}</span>
                      {recipient.client_id && (
                        <span className="text-xs text-gray-400">Client #{recipient.client_id.substring(0, 8)}</span>
                      )}
                      {recipient.sent_at && (
                        <span className="text-xs text-gray-400">Envoyé le {formatDate(recipient.sent_at)}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => copyToClipboard(recipient.to)}
                        className="p-1 text-gray-400 hover:text-primary rounded transition-colors"
                        title="Copier le numéro"
                      >
                        <Copy size={14} />
                      </button>
                      {onResend && recipient.status !== "delivered" && (
                        <button
                          onClick={() => handleResend(recipient.id)}
                          disabled={resending === recipient.id}
                          className="p-1 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors disabled:opacity-50"
                          title="Renvoyer"
                        >
                          <RefreshCw size={14} className={resending === recipient.id ? "animate-spin" : ""} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </TableRow>
  );
}
